import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { FieldPath, useController, UseControllerProps } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { clsx } from 'clsx';
import { isArray } from 'lodash';
import { faChevronDown, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmptyArray, isEmptyString, isNotEmptyArray, isNotEmptyString, isNotSet, isSet, isString } from '@/utils';
import useChangeEvent from '@/hooks/useChangeEvent';
import { useCollapse } from '@/hooks/useCollapse';
import { Input } from '@/components/common/Fields/Input';
import { MultiCheckbox } from '@/components/common/Fields/MultiCheckbox';
import { RenderDisplayProps } from '@/components/common/Fields/Select';
import { SingleCheckbox } from '@/components/common/Fields/SingleCheckbox';
import BottomSheetModal from '@/components/common/BottomSheetModal';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import Flexbox from '@/components/common/Flexbox';
type DatalistValue = string | number;
interface DatalistProps<Value> {
  chevronClassName?: string;
  className?: string;
  options: Array<Option<Value> & {
    leftSlot?: React.ReactElement;
  }>;
  placeholder?: string;
  renderDisplay?: React.FC<RenderDisplayProps<false>>;
  disableBolder?: boolean;
  disablePadding?: boolean;
  wrapperRef?: HTMLDivElement | null;
  searchHint?: string;
  onChanged?: () => void;
  testIdPrefix?: string;
  title?: string;
  inputMode?: ElementProps<'input'>['inputMode'];
  isMultiple?: boolean;
  searchable?: boolean;
  showSelectionType?: boolean;
}
type CommonDatalistProps<Value extends DatalistValue> = ({
  isMultiple?: false;
  value: Value | undefined;
  onChange: (value: Value | undefined) => void;
} | {
  isMultiple: true;
  value: Array<Value> | undefined;
  onChange: (value: Array<Value> | undefined) => void;
}) & {
  error?: unknown;
};
interface DatalistComponent extends CustomizeFunctionComponent {
  <TFormValues extends Record<string, any>, Name extends FieldPath<TFormValues>>(props: PropsWithChildren<UseControllerProps<TFormValues, Name> & DatalistProps<TFormValues[Name]>>, context?: any): React.ReactElement | null;
}
interface CommonDatalistComponent extends CustomizeFunctionComponent {
  <Value extends DatalistValue>(props: DatalistProps<Value> & CommonDatalistProps<Value>, context?: any): React.ReactElement | null;
}
export const CommonDatalist: CommonDatalistComponent = ({
  className,
  chevronClassName,
  placeholder,
  options,
  renderDisplay,
  disableBolder,
  disablePadding,
  searchHint,
  onChanged,
  testIdPrefix = '',
  title,
  inputMode,
  searchable = true,
  showSelectionType,
  error,
  ...props
}) => {
  const t = useTranslations();
  const {
    isCollapse,
    expand,
    collapse
  } = useCollapse();
  const [keyword, onKeywordChange] = useChangeEvent();
  const displayValue = useMemo(() => {
    const valueForUse = props.isMultiple ? props.value : [props.value];
    const result = options.filter(({
      key
    }) => (valueForUse as Array<string | number> ?? []).includes(key))?.map(({
      label
    }) => label).join('、');
    return isEmptyString(result) ? placeholder : result;
    // eslint-disable-next-line
  }, [props.value, props.isMultiple, options]);
  const actualOptions = useMemo(() => {
    // keyword should not start or end with space;
    const actualKeyword = keyword.trim();
    // If the keyword is empty after trimStart, return all options;
    if (isEmptyString(actualKeyword)) return options;

    // Select the options which label or key contains keyword;
    return options.filter(({
      label,
      key
    }) => {
      return label.toLocaleLowerCase().includes(actualKeyword.toLocaleLowerCase()) || isString(key) && key.toLocaleLowerCase().includes(actualKeyword.toLocaleLowerCase());
    });
  }, [keyword, options]);
  const changeHandler = useCallback((value: Array<DatalistValue> | DatalistValue | undefined) => {
    if (isArray(value)) {
      if (props.isMultiple) props.onChange(value as any);
    } else {
      if (!props.isMultiple) props.onChange(value as any);
    }
    onChanged?.();
    if (!props.isMultiple) collapse();
  }, [props.onChange, onChanged, props.isMultiple]);
  const testId = useMemo(() => {
    return `${testIdPrefix}-datalist`;
  }, [testIdPrefix]);
  return <>
      <BottomSheetModal show={!isCollapse} onClose={collapse} noSureButton size={'lg'} zIndex={200} contentClassName={'px-6'} testIdPrefix={`${testId}-`} title={title} titleRight={showSelectionType && <div className={clsx('p-2')}>
              <p className={clsx('py-0.5 px-1.5', 'rounded-s', 'bg-primary/10', 'text-primary')}>
                {t(`common.selectionType.${props.isMultiple ? 'multiple' : 'single'}`)}
              </p>
            </div>} data-sentry-element="BottomSheetModal" data-sentry-source-file="Datalist.tsx">
        <Flexbox direction={'column'} className={clsx('pt-4', 'space-y-4', 'h-full', 'overflow-hidden')} data-sentry-element="Flexbox" data-sentry-source-file="Datalist.tsx">
          {/*search bar*/}
          {searchable && <Input data-test-id={`${testId}-keyword-input`} placeholder={searchHint} value={keyword} onChange={onKeywordChange} leftSlot={<FontAwesomeIcon icon={faSearch} className={'pl-4 w-3.5 h-3.5'} />} outerWrapperClassname={clsx('')} inputMode={inputMode} />}
          <div className={clsx(searchable ? 'pt-4' : 'pt-3', 'flex-1', 'overflow-scroll')}>
            {/*result options*/}
            {isEmptyArray(actualOptions) ? <Flexbox data-test-id={`${testId}-keyword-no-result`} align={'center'} className={clsx('px-4 h-12 text-fz5', 'bg-white')} onClick={collapse}>
                {t('common.searchNoResult')}
              </Flexbox> : <div data-test-id={`${testId}-keyword-result`}>
                {props.isMultiple ? <MultiCheckbox options={actualOptions} onChange={changeHandler} value={props.value} testIdPrefix={testIdPrefix} noTopBorder /> : <SingleCheckbox testIdPrefix={`${testId}-`} options={actualOptions} value={props.value} onChange={changeHandler} noTopBorder />}
              </div>}
          </div>
        </Flexbox>
      </BottomSheetModal>
      <Flexbox align={'center'} justify={'between'} onClick={expand} className={clsx(className, 'relative', 'w-full', 'text-fz5 text-grey-2', 'bg-white', 'hover:cursor-pointer', {
      'border border-solid border-red': !disableBolder && isSet(error),
      'border border-solid border-grey5 focus-within:border-blue focus-within:shadow-[0px_0px_0px_3px_rgba(89,_123,_212,_0.10)]': !disableBolder && isNotSet(error)
    }, {
      'p-3.5': !disablePadding
    }, {
      'rounded-m': !disableBolder
    })} data-test-id={`${testId}-value`} data-sentry-element="Flexbox" data-sentry-source-file="Datalist.tsx">
        <ConditionalFragment condition={!renderDisplay} data-sentry-element="ConditionalFragment" data-sentry-source-file="Datalist.tsx">
          <Flexbox align={'center'} className={clsx('mr-2', 'text-base flex-1 truncate', {
          'text-grey3': isNotSet(props.value) || (props.isMultiple ? isEmptyArray(props.value) : isEmptyString(props.value))
        })} data-sentry-element="Flexbox" data-sentry-source-file="Datalist.tsx">
            {displayValue || placeholder}
          </Flexbox>
        </ConditionalFragment>
        <ConditionalFragment condition={isSet(renderDisplay)} data-sentry-element="ConditionalFragment" data-sentry-source-file="Datalist.tsx">
          {renderDisplay && React.createElement(renderDisplay, {
          displayValue: displayValue || placeholder,
          hasSelected: isSet(props.value) && (isString(props.value) && isNotEmptyString(props.value) || Array.isArray(props.value) && isNotEmptyArray(props.value)),
          selectedKeys: props.value as any
        })}
        </ConditionalFragment>
        <span className={clsx(chevronClassName)}>
          <FontAwesomeIcon icon={faChevronDown} className={clsx('transform transition-transform', {
          'rotate-180': !isCollapse
        })} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="Datalist.tsx" />
        </span>
      </Flexbox>
    </>;
};
export const Datalist: DatalistComponent = ({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  disabled,
  ...props
}) => {
  const {
    field: {
      value,
      onChange
    },
    fieldState: {
      error
    }
  } = useController({
    control,
    name,
    rules,
    shouldUnregister,
    defaultValue,
    disabled
  });
  return <CommonDatalist {...props} value={value} onChange={onChange} error={error} data-sentry-element="CommonDatalist" data-sentry-component="Datalist" data-sentry-source-file="Datalist.tsx" />;
};